import { navigate } from 'gatsby';
import { T, always, cond, equals, filter, find, flatten, includes, keys, map } from 'ramda';
import React, { FC, useEffect, useState } from 'react';
import { useAsync } from 'react-use';
import styled from 'styled-components';

import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import GridItem from '@ui/atoms/GridItem';
import HeadingText from '@ui/atoms/HeadingText';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import Skeleton from '@ui/atoms/Skeleton';

import TopicCardSlider from '@ui/organisms/TopicCardSlider';

import {
	DescriptionText,
	IGetCarePageContent,
} from '@ui/routes/Dashboard/GetCare/GetCare.component';
import { GetCareFlow, ITopic, dataConstants } from '@ui/routes/Dashboard/GetCare/GetCare.data';

import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import useMemberInfo from '@hooks/useMemberInfo';
import { useServiceEligibility } from '@hooks/useServiceEligibility';

import { doesNotExist } from '@utils/general';
import { getStateName } from '@utils/intake/stateRequirements';
import { isBrowser } from '@utils/isBrowser';
import { getAllStateServiceAvailability } from '@utils/service-availability';

import { useAuth } from '@providers/AuthProvider';

import { IFlowStepComponentProps } from '@folx-types/flow-steps';

import theme from '@theme/FolxTheme';

interface IMemberVHCGetCareStepProps extends IFlowStepComponentProps {
	pageContent: IGetCarePageContent;
	stateOfResidence: string;
}

const FullWidthColumn = fullWidth<IFlexColumn>(FlexColumn);
const SpacedColumn = spaced<IFlexColumn>(FullWidthColumn);

const GetCareColumn = styled(SpacedColumn)`
	padding: 0 ${theme.spacing.lg};
	@media ${theme.responsive.breakpoints.laptop} {
		max-width: 68rem;
		padding: 0;
	}
`;

const MemberVHCGetCareStep: FC<IMemberVHCGetCareStepProps> = ({
	pageContent,
	handleFormSubmission = () => {},
	stepKey,
}) => {
	const { loggedIn } = useAuth();
	const { memberInfo, loading: memberInfoLoading } = useMemberInfo();
	const { eligible, loading: eligibilityLoading } = useServiceEligibility(loggedIn);
	const { value: stateServiceAvailability, loading: stateServiceLoading } = useAsync(async () =>
		getAllStateServiceAvailability()
	);

	const [loading, setLoading] = useState<boolean>(true);
	const [isTestosteroneAvailable, setIsTestosteroneAvailable] = useState<boolean>(false);
	const [isEstrogenAvailable, setIsEstrogenAvailable] = useState<boolean>(false);
	const [isAdhdAvailable, setIsAdhdAvailable] = useState<boolean>(false);
	const [isMhmAvailable, setIsMhmAvailable] = useState<boolean>(false);

	const {
		heading,
		description,
		genderAffirmingCareLabel,
		sexualAndReproductiveHealthLabel,
		feelingIllLabel,
		fertilityAndFamilyPlanningLabel,
		inactiveTooltip,
		tooltips,
		stateTooltips,
		stateTopicTooltips,
	} = pageContent;

	useEffect(() => {
		const topicId = isBrowser && window.localStorage.getItem('vhc_topic');

		if (topicId) {
			const categories = Object.keys(dataConstants);
			const flattenedTopics = flatten(map((category) => dataConstants[category], categories));
			const { topic, ...topicProps } = find(({ id }) => `${id}` === topicId, flattenedTopics) || {};
			if (topic) {
				handleFormSubmission({ topic, mhId: topicProps?.mhId }, stepKey);
			}
		}

		isBrowser && window.localStorage.removeItem('vhc_topic');

		setLoading(false);
	}, []);

	const onTopicClick =
		({ topic, id, mhId, flow }: { topic: string; id: number; flow: string; mhId?: string }) =>
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();

			if (id == 2) {
				// Redirect to GAHT consult
				return navigate('/app/flows/gaht_consult/');
			}

			if (flow === 'mh_sub') {
				// Redirect to MH flow with stored topic
				window.localStorage.setItem('mh_topic', `${mhId}`);
				return navigate('/app/flows/mh_sub/');
			}

			handleFormSubmission({ topic, mhId }, stepKey);
		};

	const stateAbbreviation = memberInfo?.stateOfResidence?.abbreviation;
	const stateOfResidence = getStateName(stateAbbreviation);

	useEffect(() => {
		if (
			stateServiceLoading ||
			memberInfoLoading ||
			eligibilityLoading ||
			doesNotExist(stateAbbreviation)
		) {
			return;
		}

		const stateAvailabilityFlows = keys(stateServiceAvailability[stateAbbreviation]);
		setIsTestosteroneAvailable(includes(GetCareFlow.hrt_t, stateAvailabilityFlows));
		setIsEstrogenAvailable(includes(GetCareFlow.hrt_e, stateAvailabilityFlows));
		setIsAdhdAvailable(includes(GetCareFlow.adhd, stateAvailabilityFlows));
		setIsMhmAvailable(includes(GetCareFlow.mh_medication, stateAvailabilityFlows));
	}, [stateServiceLoading, memberInfoLoading]);

	return (
		<GridItem columnStart={[1, 1, 4, 4]} columnEnd={[-1, -1, 12, 11]}>
			<GetCareColumn gap="md" marginBottom="xl">
				<LoadingWrapper
					loading={memberInfoLoading || stateServiceLoading || eligibilityLoading || loading}
					skeleton={
						<FlexColumn gap="sm">
							<Skeleton fullWidth height="md" />
							<Skeleton structure="row" fullWidth repeated={2} width="xxxxxl" height="xxxl" />
						</FlexColumn>
					}>
					<HeadingText>{heading}</HeadingText>
					<DescriptionText>{description}</DescriptionText>

					<TopicCardSlider
						category={genderAffirmingCareLabel}
						state={stateOfResidence ?? 'your state'}
						onClick={onTopicClick}
						topics={filter(
							(topic: ITopic) => topic.flow === 'vc' && topic.topic !== 'Something else'
						)(
							map((topic: ITopic) => {
								const isActive = cond([
									[(t: ITopic) => equals('gaht_consult', t.path), always(eligible)],
									[
										(t: ITopic) => equals('Gender marker letter', t.topic),
										() => !includes(stateAbbreviation, topic?.unavailableStates ?? []),
									],
									[T, always(true)],
								])(topic);
								topic.isActive = isActive;
								topic.ineligible = !isActive && equals('gaht_consult', topic.path);
								return topic;
							})(dataConstants.genderAffirmingCareTopics as ITopic[])
						)}
						tooltips={tooltips}
						stateTooltips={stateTooltips}
						stateTopicTooltips={stateTopicTooltips}
						inactiveTooltip={inactiveTooltip}
					/>
					<TopicCardSlider
						category={sexualAndReproductiveHealthLabel}
						state={stateOfResidence ?? 'your state'}
						onClick={onTopicClick}
						topics={filter((topic: ITopic) => topic.topic !== 'Something else')(
							map((topic: ITopic) => {
								const isActive = cond([
									[equals('Low testosterone'), always(isTestosteroneAvailable)],
									[equals('Menopause'), always(isEstrogenAvailable)],
									[T, always(true)],
								])(topic.topic);
								topic.isActive = isActive;
								return topic;
							})(dataConstants.sexualAndReproductiveHealthTopics as ITopic[])
						)}
						tooltips={tooltips}
						stateTooltips={stateTooltips}
						stateTopicTooltips={stateTopicTooltips}
						inactiveTooltip={inactiveTooltip}
					/>
					<TopicCardSlider
						category={feelingIllLabel}
						state={stateOfResidence ?? 'your state'}
						onClick={onTopicClick}
						topics={filter((topic) => topic.topic !== 'Something else')(
							map((topic: ITopic) => {
								const isActive = cond([
									[(t: ITopic) => equals(GetCareFlow.adhd, t.flow), always(isAdhdAvailable)],
									[
										(t: ITopic) => equals(GetCareFlow.mh_medication, t.flow),
										always(isMhmAvailable),
									],
									[
										(t: ITopic) => equals('Gender marker letter', t.topic),
										() => !includes(stateAbbreviation, topic?.unavailableStates ?? []),
									],
									[T, always(true)],
								])(topic);
								topic.isActive = isActive;
								return topic;
							})(dataConstants.feelingIllTopics)
						)}
						tooltips={tooltips}
						stateTooltips={stateTooltips}
						stateTopicTooltips={stateTopicTooltips}
						inactiveTooltip={inactiveTooltip}
					/>
					<TopicCardSlider
						category={fertilityAndFamilyPlanningLabel}
						state={stateOfResidence ?? 'your state'}
						onClick={onTopicClick}
						topics={dataConstants.fertilityAndFamilyBuilding}
						tooltips={tooltips}
						stateTooltips={stateTooltips}
						stateTopicTooltips={stateTopicTooltips}
						inactiveTooltip={inactiveTooltip}
					/>
				</LoadingWrapper>
			</GetCareColumn>
		</GridItem>
	);
};

export default MemberVHCGetCareStep;
